//
// vessels.ts — the one feed that is still not real
//
// Translation of the vessel half of `Networking/StaticFeedService.swift`.
//
// **This data is fixed and invented.** The original Fuser server never
// called an AIS API despite the UI implying live tracking, and every free
// alternative surveyed for the 2026-08-21 live-data build turned out to be
// paid (MarineTraffic, Datalastic, Spire) or receiver-gated (AISHub, which
// requires you to contribute a receiver feed before it will serve you one).
// Aircraft, satellites, weather and places all became real in that build.
// Ships did not, and pretending otherwise would be the exact mistake this
// project already had to unwind once.
//
// The `dataSource` strings below deliberately say "AIS" because they are
// what the original showed. Everything here is honest fiction, and the
// panel that displays it is the place to say so if that ever matters.
//
// To make it real: swap the body of `fetchVessels` for a provider call. The
// `TrackedObject` shape does not need to change.
//
// **Build 1.5:** each ship is now tagged with which of SEA's four
// second-tier buttons owns it, so the menu filters them the way it will
// filter real ones. The buttons are the AIS ship-type bands, so the tags
// are the bands these ships would actually broadcast. Nothing under
// Fishing & Working, because none of the six is a fishing or working boat
// — an empty button is the honest outcome there.
//
// **USS GERALD R. FORD was removed.** There is no military boats button and
// there will not be one: warships switch their AIS transponders off, so a
// carrier reporting a public position is not a thing that happens. It was
// the most convincing-looking record in a list of invented ones. See
// `docs/builds/1.5/spec.md` #7.
//

import type { TrackedObject } from "../types";

export async function fetchVessels(): Promise<TrackedObject[]> {
  return [
    {
      id: "vs-evergiven", layer: "seaCargo", mmsi: "353136000", name: "EVER GIVEN",
      category: "vessel", typeLabel: "CONTAINER SHIP (20,124 TEU)",
      latitude: 12.8, longitude: 43.2,
      speedKnots: 18.4, headingDeg: 310,
      origin: "YANTIAN (China)", destination: "ROTTERDAM (Netherlands)",
      vesselType: "Ultra Large Container Vessel",
      dataSource: "AIS Terrestrial & Satellite", lastUpdated: "Live AIS (Bab al-Mandeb)",
    },
    {
      id: "vs-maersk-mckinney", layer: "seaCargo", mmsi: "219018271", name: "MAERSK MC-KINNEY MOLLER",
      category: "vessel", typeLabel: "TRIPLE-E CONTAINER VESSEL",
      latitude: 5.8, longitude: 98.4,
      speedKnots: 19.1, headingDeg: 295,
      origin: "SINGAPORE", destination: "HAMBURG (Germany)",
      vesselType: "Triple-E Container Ship",
      dataSource: "Satellite AIS Network", lastUpdated: "Live AIS (Malacca Strait)",
    },
    {
      id: "vs-msc-oscar", layer: "seaCargo", mmsi: "352223000", name: "MSC OSCAR",
      category: "vessel", typeLabel: "MEGA CONTAINER SHIP (19,224 TEU)",
      latitude: 32.5, longitude: -155.2,
      speedKnots: 21.0, headingDeg: 78,
      origin: "SHANGHAI (China)", destination: "LONG BEACH (USA)",
      vesselType: "Container Ship",
      dataSource: "Satellite AIS Network", lastUpdated: "Live AIS (North Pacific)",
    },
    {
      id: "vs-seawise", layer: "seaTanker", mmsi: "538004112", name: "TI OCEANIA (VLCC TANKER)",
      category: "vessel", typeLabel: "VERY LARGE CRUDE CARRIER (VLCC)",
      latitude: -6.2, longitude: 105.8,
      speedKnots: 14.2, headingDeg: 62,
      origin: "RAS TANURA (Saudi Arabia)", destination: "YOKOHAMA (Japan)",
      vesselType: "Supertanker",
      dataSource: "AIS Coastal Tracking", lastUpdated: "Live AIS (Sunda Strait)",
    },
    {
      id: "vs-queenmary2", layer: "seaPassenger", mmsi: "310627000", name: "QUEEN MARY 2",
      category: "vessel", typeLabel: "OCEAN LINER PASSENGER VESSEL",
      latitude: 44.8, longitude: -48.2,
      speedKnots: 24.5, headingDeg: 252,
      origin: "SOUTHAMPTON (UK)", destination: "NEW YORK (USA)",
      vesselType: "Transatlantic Ocean Liner",
      dataSource: "Satellite AIS", lastUpdated: "Live AIS (Grand Banks)",
    },
  ];
}
